import React, { useEffect, useRef, useState } from 'react';
import { AlertResult, MatchType, VarianceType, WML } from 'src/components/ProductAlerts/types/AlertResults';

export type AlertResultsComparisonProps = {
  records: AlertResult[] | null;
  total: number;
  insertionDate: string | null;
  alertId: string | null;
  sourceOwnerId: string | null;
  uniqueIdentifier: string | null;
  wml: keyof typeof WML | null;
  variance: [number, number] | null;
  matchType: MatchType | null;
  priceType: VarianceType | null;
  competitorSiteId: string | null;
  competitorSellerName: string | null;
  competitorFulfillerName: string | null;
};

export const AlertResultsComparison: React.FC<AlertResultsComparisonProps> = ({
  records,
  total,
  insertionDate,
  alertId,
  sourceOwnerId,
  uniqueIdentifier,
  wml,
  variance,
  matchType,
  priceType,
  competitorSiteId,
  competitorSellerName,
  competitorFulfillerName,
}) => {
  const action = useRef<'reset' | 'install' | null>(null);
  const [prevTotal, setPrevTotal] = useState<number | null>(null);
  const [currentTotal, setCurrentTotal] = useState<number | null>(null);

  const resetTotals = () => {
    setPrevTotal(null);
    setCurrentTotal(total);
  };

  const installTotals = () => {
    setPrevTotal(currentTotal);
    setCurrentTotal(total);
  };

  useEffect(() => {
    action.current = 'reset';
  }, [
    alertId,
    sourceOwnerId,
    uniqueIdentifier,
    wml,
    variance,
    matchType,
    priceType,
    competitorSiteId,
    competitorSellerName,
    competitorFulfillerName,
  ]);

  useEffect(() => {
    if (!action.current) {
      action.current = 'install';
    }
  }, [insertionDate]);

  useEffect(() => {
    switch (action.current) {
      case 'reset':
        resetTotals();
        break;
      case 'install':
        installTotals();
        break;
    }
    action.current = null;
  }, [records]);

  if (prevTotal !== null && currentTotal !== null) {
    const difference = currentTotal - prevTotal;
    return (
      <div>
        {difference === 0
          ? `No change compared to the previous date`
          : `${Math.abs(difference)} ${difference > 0 ? 'more' : 'fewer'} results compared to the previous date`}
      </div>
    );
  }

  return null;
};
